export interface DamageItem {
  description: string;
  location: string;
  severity: 'minor' | 'moderate' | 'severe';
  photo_url?: string;
}

export interface AdditionalCost {
  description: string;
  amount_cents: number;
}

export interface PickupProtocol {
  mileage: number;
  fuel_level: number;
  damages: DamageItem[];
  photos: string[];
  notes?: string;
}

export interface ReturnProtocol {
  mileage: number;
  fuel_level: number;
  damages: DamageItem[];
  photos: string[];
  notes?: string;
  refuel_required: boolean;
  cleaning_required: boolean;
}

export interface Contract {
  id: number;
  tenant_id: number;

  // Contract number
  contract_number: string;

  // Relations
  booking_id: number | null;
  customer_id: number;
  vehicle_id: number;

  // Contract data
  contract_data: Record<string, unknown>;

  // Pickup protocol
  pickup_protocol: PickupProtocol | null;
  pickup_signature_customer: string | null;
  pickup_signature_employee: string | null;
  pickup_at: string | null;
  pickup_by: string | null;

  // Return protocol
  return_protocol: ReturnProtocol | null;
  return_signature_customer: string | null;
  return_signature_employee: string | null;
  return_at: string | null;
  return_by: string | null;

  // Mileage
  mileage_start: number | null;
  mileage_end: number | null;
  mileage_driven: number | null;

  // Fuel
  fuel_level_start: number | null;
  fuel_level_end: number | null;
  refuel_required: boolean;
  refuel_cost_cents: number | null;

  // Damages
  damages_at_pickup: DamageItem[];
  damages_at_return: DamageItem[];
  damage_cost_cents: number;

  // Additional costs
  additional_km_driven: number;
  additional_km_cost_cents: number;
  cleaning_cost_cents: number;
  other_costs: AdditionalCost[];

  // Total
  final_total_cents: number | null;

  // PDF
  pdf_url: string | null;

  // Status
  status: 'active' | 'completed' | 'disputed';

  // Timestamps
  created_at: string;
  updated_at: string;

  // Related data (optional)
  customer_name?: string;
  vehicle_name?: string;
}

export interface ContractCreate {
  contract_number: string;
  booking_id?: number;
  customer_id: number;
  vehicle_id: number;
  contract_data: Record<string, unknown>;
  status?: 'active' | 'completed' | 'disputed';
}

export type ContractUpdate = Partial<ContractCreate>;

export interface ContractListResponse {
  contracts: Contract[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Helper function to format price from cents to EUR
export function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2) + ' €';
}

// Status translations
export const CONTRACT_STATUS_LABELS: Record<Contract['status'], string> = {
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  disputed: 'Umstritten',
};

// Status colors for badges
export const CONTRACT_STATUS_COLORS: Record<Contract['status'], string> = {
  active: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  completed: 'bg-green-500/15 text-green-600 dark:text-green-400',
  disputed: 'bg-red-500/15 text-red-600 dark:text-red-400',
};
